import React, { useEffect, useState } from 'react';
import { Layout, Menu, Badge, Dropdown, Avatar, List, Typography, Empty, Button } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UnorderedListOutlined,
  BarChartOutlined,
  TeamOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  ProjectOutlined,
  BookOutlined,
  ApartmentOutlined,
  NotificationOutlined,
  CheckCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface Notification {
  id: number;
  task_id: number;
  type: string;
  title: string;
  content: string;
  read: number;
  created_at: string;
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications?unread_only=true');
      setNotifications(res.data);
      setUnreadCount(res.data.length);
    } catch (error) {
      console.error('获取通知失败', error);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await api.post(`/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('标记已读失败', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      fetchNotifications();
    } catch (error) {
      console.error('标记全部已读失败', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: '工作台', roles: ['admin', 'supervisor', 'leader', 'teacher'] },
    { key: '/tasks', icon: <UnorderedListOutlined />, label: '任务列表', roles: ['admin', 'supervisor', 'leader', 'teacher'] },
    { key: '/personal-tasks', icon: <CheckCircleOutlined />, label: '个人任务', roles: ['admin', 'supervisor', 'leader', 'teacher'] },
    { key: '/statistics', icon: <BarChartOutlined />, label: '统计视图', roles: ['admin', 'supervisor', 'leader'] },
    { key: '/announcements', icon: <NotificationOutlined />, label: '公告', roles: ['admin', 'supervisor', 'leader', 'teacher'] },
    { key: '/projects', icon: <ProjectOutlined />, label: '项目管理', roles: ['admin', 'supervisor'] },
    { key: '/subjects', icon: <BookOutlined />, label: '科目管理', roles: ['admin', 'supervisor'] },
    { key: '/groups', icon: <ApartmentOutlined />, label: '所属组管理', roles: ['admin', 'supervisor'] },
    { key: '/users', icon: <TeamOutlined />, label: '成员管理', roles: ['admin'] },
  ];

  const filteredItems = menuItems.filter(item => item.roles.includes(user?.role || ''));

  const userMenuItems = [
    { key: 'profile', label: '个人中心', icon: <UserOutlined /> },
    { key: 'logout', label: '退出登录', icon: <LogoutOutlined />, danger: true },
  ];

  const handleMenuClick = (key: string) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: '总负责人',
      supervisor: '主管',
      leader: '组长',
      teacher: '教师',
    };
    return labels[role] || role;
  };

  const notificationDropdownContent = (
    <div style={{ width: 360, maxHeight: 400, overflow: 'auto', background: '#fff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong>通知</Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={markAllAsRead}>
            全部已读
          </Button>
        )}
      </div>
      {notifications.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无未读通知" style={{ padding: 24 }} />
      ) : (
        <List
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item
              style={{ padding: '12px 16px', cursor: 'pointer', background: item.read ? '#fff' : '#e6f7ff' }}
              onClick={() => {
                markAsRead(item.id);
                if (item.task_id) {
                  navigate(`/tasks/${item.task_id}`);
                }
                setNotifOpen(false);
              }}
            >
              <List.Item.Meta
                title={<Text strong>{item.title}</Text>}
                description={
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{item.content}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {new Date(item.created_at).toLocaleString()}
                      </Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider theme="dark" width={200} collapsed={collapsed} onCollapse={setCollapsed} style={{ background: '#43a047', position: 'relative', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: collapsed ? '20px 8px' : '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          {!collapsed && <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>教研任务系统</span>}
          <div style={{ cursor: 'pointer', flexShrink: 0, marginLeft: collapsed ? 'auto' : 0, marginRight: collapsed ? 'auto' : 0 }} onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <MenuUnfoldOutlined style={{ color: '#fff', fontSize: 18 }} /> : <MenuFoldOutlined style={{ color: '#fff', fontSize: 18 }} />}
          </div>
        </div>
        <Menu
          mode="inline"
          theme="dark"
          style={{ background: '#43a047', fontWeight: 'bold' }}
          selectedKeys={[location.pathname]}
          items={filteredItems.map(item => ({
            key: item.key,
            icon: item.icon,
            label: <span style={{ fontWeight: 'bold' }}>{item.label}</span>,
            onClick: () => navigate(item.key),
          }))}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#43a047', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
          <div />
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#fff' }}>
            <Dropdown
              open={notifOpen}
              onOpenChange={setNotifOpen}
              dropdownRender={() => notificationDropdownContent}
              placement="bottomRight"
              trigger={['click']}
            >
              <Badge count={unreadCount} overflowCount={99}>
                <BellOutlined style={{ fontSize: '20px', cursor: 'pointer', color: '#fff' }} />
              </Badge>
            </Dropdown>
            <Dropdown
              menu={{ items: userMenuItems.map(item => ({
                key: item.key,
                icon: item.icon,
                label: item.label,
                danger: item.danger,
              })), onClick: ({ key }) => handleMenuClick(key) }}
              placement="bottomRight"
            >
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#fff' }}>
                <Avatar icon={<UserOutlined />} style={{ background: '#fff', color: '#43a047' }} />
                <span>{user?.name} ({getRoleLabel(user?.role || '')})</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: '24px', padding: '24px', background: '#fff', borderRadius: '8px', overflow: 'auto' }}>
          {children}
        </Content>
      </Layout>
      <img
        src="/working-cute.jpg"
        alt="疯狂工作中"
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '16px',
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          border: '3px solid rgba(255,255,255,0.7)',
          objectFit: 'cover',
          animation: 'wiggle 2s ease-in-out infinite',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          cursor: 'pointer',
          zIndex: 999,
        }}
        title="疯狂工作中..."
      />
    </Layout>
  );
}
